import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useGetPositionLookupParamOptions } from '@/services/hooks/relay/positions'
import {
  LookupParam,
  ParamOption,
  PositionLookup,
} from '@/types/position-lookups'
import { dependsOnValues } from '@/utils/positions'

type PositionParamFieldProps = {
  lookup: PositionLookup
  param: LookupParam
  /** Every param on the position, so this one can read what it depends on. */
  params: Record<string, string>
  /**
   * The data-provider org whose ERP data the choices are read from. Empty
   * while it is still being looked up, which keeps the fetch disabled.
   */
  orgId: string
  orgCredentialTypeId: string
  onChange: (value: string) => void
}

/**
 * One blank a lookup asks to be filled in. Its choices are either listed by
 * the accounting system up front or fetched from the customer's own data.
 * Where the accounting system can only report the values people hold today, a
 * typed value is accepted too, so a job nobody holds yet can still be set up.
 */
export default function PositionParamField({
  lookup,
  param,
  params,
  orgId,
  orgCredentialTypeId,
  onChange,
}: PositionParamFieldProps) {
  const value = params[param.name] ?? ''
  const isLive = param.options.kind === 'live'
  const dependsOn = dependsOnValues(param, params)
  const missingDependency = Object.values(dependsOn).some((it) => !it)

  const { paramOptions, isFetching } = useGetPositionLookupParamOptions({
    lookupKey: lookup.key,
    paramName: param.name,
    orgId,
    orgCredentialTypeId,
    dependsOnParams: dependsOn,
    enabled: isLive,
  })

  const options: ParamOption[] =
    param.options.kind === 'static' ? param.options.values : paramOptions

  // A saved value the accounting system hasn't offered back still has to
  // show: the request may have failed, not run yet, or the value was typed in.
  // An empty box next to a working Save button looks like nothing was set, and
  // picking again makes a new version of the position for no reason.
  const selected =
    options.find((option) => option.value === value) ??
    (value ? { value, label: value, detail: null } : null)
  const allowCustom = param.options.allow_custom

  const dependencyLabels = Object.keys(dependsOn)
    .map(
      (name) =>
        lookup.params.find((other) => other.name === name)?.label ?? name,
    )
    .join(' and ')

  // Only the blanks that cannot be filled in yet say anything. The catalog's
  // own description of a param repeats its label more often than it helps.
  const helperText = missingDependency
    ? `Choose ${dependencyLabels} first.`
    : undefined

  const sharedProps = {
    options,
    disabled: missingDependency,
    loading: isFetching,
    renderOption: (
      optionProps: React.HTMLAttributes<HTMLLIElement> & { key?: string },
      option: ParamOption,
    ) => (
      <li {...optionProps} key={option.value}>
        <Stack spacing={0.25}>
          <Typography variant="body2">{option.label}</Typography>
          {option.detail && (
            <Typography variant="caption" color="text.secondary">
              {option.detail}
            </Typography>
          )}
        </Stack>
      </li>
    ),
    renderInput: (inputProps: AutocompleteRenderInputParams) => (
      <TextField
        {...inputProps}
        label={param.label}
        required={param.required}
        helperText={helperText}
      />
    ),
  }

  // Two shapes, because the value means different things. Where the
  // accounting system only reports the values people hold today, the position
  // may name one nobody holds yet: the typed text is the value and the listed
  // choices are only suggestions. Everywhere else the value is one of the
  // choices.
  if (allowCustom) {
    return (
      <Autocomplete
        {...sharedProps}
        freeSolo
        value={value}
        inputValue={value}
        // Only what someone types. Picking a choice fires this too, carrying
        // the words shown for it rather than the value it stands for, and the
        // two are not always the same string.
        onInputChange={(_event, next, reason) => {
          if (reason === 'input') onChange(next)
        }}
        onChange={(_event, next) =>
          onChange(typeof next === 'string' ? next : next?.value ?? '')
        }
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.label
        }
        noOptionsText="No values reported yet — type the value to use"
      />
    )
  }

  return (
    <Autocomplete
      {...sharedProps}
      value={selected}
      onChange={(_event, next) => onChange(next?.value ?? '')}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selectedOption) =>
        option.value === selectedOption.value
      }
      noOptionsText="Nothing available for this field"
    />
  )
}
